<script setup lang="ts">
/**
 * Generic "fund inside the pot" breakdown (2026-10-13) — the successor of the Japan tab's "Japan leg"
 * view, for any fund. One row per fund (contribution, share of pot P&L, deals, days held, costs,
 * distributions); clicking a row opens its cumulative contribution, P&L by year and deal list.
 * Data: FofTableResult.funds (C# SharedPotFundBreakdown), Live and Candidate tables only.
 */
import { computed, ref, watch } from 'vue'
import type { SharedPotFundDetail } from '../../api/client'
import { linearChart } from '../japan/sharedPotChart'

const props = defineProps<{ funds: SharedPotFundDetail[]; dates: string[] }>()

const TRADES_SHOWN = 20
const NAMES: Record<string, string> = {
  BGA: 'BGA', JH: 'JH', GOLD: 'Gold', GGE: 'GGE', JAPAN: 'L&G Japan', CHARTERIS: 'Silver (Charteris)', AMATI: 'Copper (Amati)',
  MINERS: 'Miners (Baker Steel)', JAPANESG: 'Japan ESG (L&G)',
  TECH: 'Tech (Liontrust)',
  ELECTRUM: 'Electrum (Baker Steel)',
  JHTECH: 'JHTech (Janus Henderson)', ASIATECH: 'AsiaTech (L&G Pacific)',
  BARINGSOIL: 'BaringsOil (Barings)', SPWORLD: 'SPWorld (Fidelity)',
  SMALLCAP: 'SmallCap (CT US Smaller Companies)',
}
function name(f: SharedPotFundDetail): string { return NAMES[f.fund] ?? f.label ?? f.fund }

const rows = computed(() => [...props.funds].sort((a, b) => b.contributionGbp - a.contributionGbp))
const selectedId = ref<string | null>(null)
const showAllDeals = ref(false)
const selected = computed(() => props.funds.find(f => f.fund === selectedId.value) ?? null)

// A different table (Live ⇄ Candidate) may not hold the open fund: close it rather than show a stale row.
// A poll that re-sends the same table leaves the selection — and "Show all" — as they are.
watch(() => props.funds, () => {
  if (selectedId.value && !props.funds.some(f => f.fund === selectedId.value)) selectedId.value = null
})
watch(selectedId, () => { showAllDeals.value = false })

function toggle(fund: string): void {
  selectedId.value = selectedId.value === fund ? null : fund
}

const chart = computed(() => selected.value ? linearChart(props.dates, selected.value.cumulativeContributionGbp) : null)
const years = computed(() => selected.value
  ? Object.entries(selected.value.byYearGbp).sort(([a], [b]) => a.localeCompare(b)) : [])
const dealsNewestFirst = computed(() => selected.value ? [...selected.value.deals].reverse() : [])
const dealsShown = computed(() => showAllDeals.value ? dealsNewestFirst.value : dealsNewestFirst.value.slice(0, TRADES_SHOWN))

function gbp(v: number): string {
  const sign = v < 0 ? '−' : ''
  return `${sign}£${Math.abs(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
}
function share(v: number | null): string { return v === null ? '—' : `${v.toFixed(1)} %` }
</script>

<template>
  <div class="fip">
    <div class="fip-scroll">
      <table class="fip-table" data-testid="fip-table">
        <thead>
          <tr><th>Fund</th><th>Contribution</th><th>Share</th><th>Deals</th><th>Days held</th><th>Costs</th><th>Distributions</th></tr>
        </thead>
        <tbody>
          <tr v-for="f in rows" :key="f.fund" data-testid="fip-row" :data-fund="f.fund"
              :class="{ on: f.fund === selectedId }" @click="toggle(f.fund)">
            <td>{{ name(f) }}</td>
            <td :class="{ neg: f.contributionGbp < 0 }">{{ gbp(f.contributionGbp) }}</td>
            <td>{{ share(f.shareOfPotPct) }}</td>
            <td>{{ f.trades }}</td>
            <td>{{ f.daysHeld }}</td>
            <td>{{ gbp(f.dealingCostsGbp + f.platformFeesGbp) }}</td>
            <td>{{ gbp(f.distributionsGbp) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="fip-hint">Tap a fund for its contribution over time, by year and its deals.</p>

    <section v-if="selected" class="fip-detail" data-testid="fip-detail">
      <h4>{{ name(selected) }} inside the pot</h4>
      <svg v-if="chart" :viewBox="chart.viewBox" class="fip-svg" role="img"
           :aria-label="`${name(selected)} cumulative contribution`" data-testid="fip-chart">
        <line v-for="t in chart.yTicks" :key="`g${t.label}`" x1="48" x2="592" :y1="t.pos" :y2="t.pos" class="grid" />
        <text v-for="t in chart.yTicks" :key="`y${t.label}`" x="44" :y="t.pos + 3" class="tick" text-anchor="end">{{ t.label }}</text>
        <text v-for="t in chart.xTicks" :key="`x${t.label}`" :x="t.pos" y="216" class="tick">{{ t.label }}</text>
        <polyline :points="chart.lines[0]" class="line" />
      </svg>
      <table class="fip-table fip-years">
        <thead><tr><th>Year</th><th>P&amp;L</th></tr></thead>
        <tbody>
          <tr v-for="[y, v] in years" :key="y" data-testid="fip-year-row"><td>{{ y }}</td><td :class="{ neg: v < 0 }">{{ gbp(v) }}</td></tr>
        </tbody>
      </table>
      <h4>Deals ({{ selected.deals.length }}, newest first)</h4>
      <p v-if="selected.deals.length === 0" class="fip-hint">This fund never dealt in this span.</p>
      <ul class="fip-deals">
        <li v-for="(d, i) in dealsShown" :key="`${d.date}-${d.kind}-${i}`" data-testid="fip-deal">
          <span>{{ d.date }}</span><span :class="['kind', d.kind.toLowerCase()]">{{ d.kind }}</span><span>{{ gbp(d.amountGbp) }}</span>
        </li>
      </ul>
      <button v-if="selected.deals.length > TRADES_SHOWN" type="button" class="fip-more" data-testid="fip-more"
              @click="showAllDeals = !showAllDeals">
        {{ showAllDeals ? 'Show fewer' : `Show all ${selected.deals.length}` }}
      </button>
    </section>
  </div>
</template>

<style scoped>
.fip { min-width: 0; }
/* Mobile-first: the table scrolls inside its card, never the page. */
.fip-scroll { overflow-x: auto; }
.fip-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; font-variant-numeric: tabular-nums; }
.fip-table th, .fip-table td { padding: 0.35rem 0.4rem; text-align: right; border-bottom: 1px solid #2a2a2a; white-space: nowrap; }
.fip-table th:first-child, .fip-table td:first-child { text-align: left; }
.fip-table tbody tr[data-testid="fip-row"] { cursor: pointer; }
.fip-table tbody tr.on td { background: #22313a; }
.neg { color: #e57373; }
.fip-hint { font-size: 0.75rem; color: #888; margin: 0.4rem 0 0.75rem; }
.fip-detail { border-top: 1px solid #333; padding-top: 0.5rem; }
.fip-detail h4 { color: #aaa; font-size: 0.8rem; margin: 0.5rem 0; text-transform: uppercase; letter-spacing: 1px; }
.fip-years { max-width: 320px; margin-top: 0.5rem; }
.fip-svg { width: 100%; height: auto; display: block; }
.fip-svg .grid { stroke: #2a2a2a; stroke-width: 1; }
.fip-svg .tick { fill: #888; font-size: 10px; }
.fip-svg .line { fill: none; stroke-width: 1.5; stroke: #4caf50; }
.fip-deals { list-style: none; padding: 0; margin: 0; }
.fip-deals li { display: grid; grid-template-columns: 1fr auto auto; gap: 0.75rem; padding: 0.4rem 0; border-bottom: 1px solid #2a2a2a; font-size: 0.85rem; font-variant-numeric: tabular-nums; }
.kind.buy, .kind.topup { color: #4caf50; }
.kind.sell, .kind.evict { color: #e57373; }
.fip-more { min-height: 44px; width: 100%; margin-top: 0.5rem; background: #222; color: #ccc; border: 1px solid #444; border-radius: 4px; cursor: pointer; }
</style>
